import React from 'react';
import { PhoneOff } from 'lucide-react';

const AgentEndCallTab = ({ agent, formData, setFormData }) => {
  const agentEndCallEnabled = formData.agent_end_call_enabled ?? false;
  const speakBeforeExecution = formData.agent_end_call_speak_before ?? false;
  const messageType = formData.agent_end_call_message_type || 'prompt';
  const promptMessage = formData._end_call_prompt_draft ?? (messageType === 'prompt' ? (formData.agent_end_call_message || '') : '');
  const staticMessage = formData._end_call_static_draft ?? (messageType === 'static' ? (formData.agent_end_call_message || '') : '');

  const handleToggleAgentEndCall = (e) => {
    e.preventDefault();
    const newEnabled = !agentEndCallEnabled;
    setFormData({
      ...formData,
      agent_end_call_enabled: newEnabled,
      ...(newEnabled ? {} : {
        agent_end_call_speak_before: false,
        agent_end_call_message: '',
        agent_end_call_message_type: 'prompt',
        _end_call_prompt_draft: '',
        _end_call_static_draft: '',
      })
    });
  };

  const handleToggleSpeakBefore = (e) => {
    e.preventDefault();
    setFormData({
      ...formData,
      agent_end_call_speak_before: !speakBeforeExecution
    });
  };

  const handleMessageTypeChange = (type) => {
    const activeMessage = type === 'prompt' ? promptMessage : staticMessage;
    setFormData({
      ...formData,
      agent_end_call_message_type: type,
      agent_end_call_message: activeMessage,
    });
  };

  const handleMessageChange = (e) => {
    const val = e.target.value;
    const draftKey = messageType === 'prompt' ? '_end_call_prompt_draft' : '_end_call_static_draft';
    setFormData({
      ...formData,
      agent_end_call_message: val,
      [draftKey]: val,
    });
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0 p-6"
      style={{ 
        scrollBehavior: 'smooth',
        maxHeight: 'calc(100vh - 280px)'
      }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
              <PhoneOff className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Agent End Call</h3>
              <p className="text-sm text-muted-foreground">Configure when and how the agent ends the call</p>
            </div>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
            <div className="min-w-0 pr-4">
              <label className="text-sm font-medium text-foreground">Enable Agent End Call</label>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, the agent ends the call in two situations: the caller says goodbye
                (e.g., &quot;bye&quot;, &quot;goodbye&quot;), or the conversation reaches a natural end
                and the caller confirms they need nothing else. The agent always asks a closing
                question such as &quot;Is there anything else I can help you with?&quot; and waits for
                the answer before hanging up.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Natural-end detection is judged against this agent&apos;s system prompt. Applies to
                inbound, outbound, web test calls, and all voice embeds.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleAgentEndCall}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                agentEndCallEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                  agentEndCallEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Speak Before Execution - only when agent end call is enabled */}
          {agentEndCallEnabled && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                <div className="min-w-0 pr-4">
                  <label className="text-sm font-medium text-foreground">Speak Before Execution</label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Before ending the call, the agent can say a closing message like: &quot;Thank you for calling. Goodbye!&quot;
                    Leave this off and the agent writes its own short farewell for each call.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSpeakBefore}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    speakBeforeExecution ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                      speakBeforeExecution ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Message type and input - only when speak before execution is enabled */}
              {speakBeforeExecution && (
                <div className="p-4 bg-muted/20 rounded-lg border border-border/50 space-y-4">
                  {/* Prompt vs Static Sentence tabs */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleMessageTypeChange('prompt')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        messageType === 'prompt'
                          ? 'bg-muted text-foreground border border-border'
                          : 'bg-background text-muted-foreground border border-border hover:bg-muted/50'
                      }`}
                    >
                      Prompt
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMessageTypeChange('static')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        messageType === 'static'
                          ? 'bg-muted text-foreground border border-border'
                          : 'bg-background text-muted-foreground border border-border hover:bg-muted/50'
                      }`}
                    >
                      Static Sentence
                    </button>
                  </div>

                  {/* Message input */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {messageType === 'prompt' ? 'Prompt for closing message' : 'Static closing message'}
                    </label>
                    <textarea
                      value={messageType === 'prompt' ? promptMessage : staticMessage}
                      onChange={handleMessageChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      placeholder={messageType === 'prompt' 
                        ? 'e.g., Say a warm goodbye and thank the caller for their time' 
                        : 'e.g., Thank you for calling. Goodbye!'}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {messageType === 'prompt' 
                        ? 'The AI will generate a closing message based on this prompt' 
                        : 'The agent will say this exact message before ending the call'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentEndCallTab;
